// src/constants/status.js
import { ROLE_ADMIN, ROLE_GL, ROLE_PL, ROLE_SEK } from "./roles.js";
import { SERVICE_PROJEKT_STATUS_VALUES, SERVICE_PROJEKT_STATUS_ALIAS_ENTRIES } from "@shared/constants/status/serviceProjekt.js";
import {
  MAENGEL_STATUS_VALUES,
  MAENGEL_STATUS_ALIAS_ENTRIES,
  MAENGEL_ALLOWED_TRANSITIONS_ENTRIES,
  buildMaengelTransitionsMap,
} from "@shared/constants/status/maengel.js";
import {
  OFFERTE_STATUS_VALUES as OFFERTE_STATUS_VALUES_SHARED,
  OFFERTE_STATUS_ALIAS_ENTRIES,
} from "@shared/constants/status/offerte.js";

/** 🔹 Definierte Statuswerte (Service/Projekt) – Single Source: @shared */
export const STATUS_VALUES = [...SERVICE_PROJEKT_STATUS_VALUES];


/** 🔹 Optionen für Select-Elemente */
export const STATUS_OPTIONS = STATUS_VALUES.map((s) => ({ value: s, label: s }));

/** 🔹 Farbdefinitionen (Tailwind Utility-Klassen) */
export const STATUS_COLORS = {
  Offen:                "bg-blue-500/15 text-blue-100 border-blue-400/30",     
  Zugeteilt:            "bg-sky-500/20 text-sky-200 border-sky-400/30",
  "In Arbeit":          "bg-amber-500/20 text-amber-200 border-amber-400/30",
  "Arbeiten erledigt":  "bg-lime-500/20 text-lime-200 border-lime-400/30",
  "Rechnung geschickt": "bg-teal-500/20 text-teal-200 border-teal-400/30",
  Abgeschlossen:        "bg-green-500/25 text-green-200 border-green-400/30",
  Storniert:            "bg-red-500/25 text-red-200 border-red-400/30",
  Archiviert:           "bg-zinc-500/25 text-zinc-300 border-zinc-400/30",
  /* Mängel-spezifisch */
  "Mängel behoben":         "bg-green-500/25 text-green-200 border-green-400/30",
  "Bericht gesendet":       "bg-indigo-500/25 text-indigo-200 border-indigo-400/30",
  "Bericht zurückgeschickt":"bg-indigo-500/25 text-indigo-200 border-indigo-400/30",
};

/* ───────────── Utility-Funktionen ───────────── */

/** 🔒 Strikte Normalisierung ohne fuzzy includes */
export const STATUS_ALIAS = new Map(SERVICE_PROJEKT_STATUS_ALIAS_ENTRIES);

export function normalizeStatus(s) {
  if (s == null) return "";
  const raw = String(s).trim();
  if (!raw) return "";
  const exact = STATUS_VALUES.find((x) => x.toLowerCase() === raw.toLowerCase());
  if (exact) return exact;
  const alias = STATUS_ALIAS.get(raw.toLowerCase());
  return alias || raw; // nur bei bekannter Map umbiegen, sonst Rohwert zurück
}

/** Gibt die passende CSS-Klasse zurück (immer gültig, fallback neutral) */
export function getStatusColor(status) {
  const s = normalizeStatus(status);
  return STATUS_COLORS[s] || "bg-white/10 text-blue-100 border-white/15";
}

/** Lesbare Form (aktuell identisch, aber zukunftssicher für Übersetzungen) */
export function prettyStatus(status) {
  return normalizeStatus(status);
}

/** Gruppenzuordnung */
export const STATUS_GROUPS = {
  active:       ["Offen", "Zugeteilt", "In Arbeit"],
  done:         ["Arbeiten erledigt", "Rechnung geschickt", "Abgeschlossen"],
  canceled:     ["Storniert"],
  archived:     ["Archiviert"],
};

/** Ist der Status aktiv (offen / läuft)? */
export function isActiveStatus(status) {
  return STATUS_GROUPS.active.includes(normalizeStatus(status));
}

/** Ist der Status abgeschlossen / fertiggestellt? */
export function isFinalStatus(status) {
  return (
    STATUS_GROUPS.done.includes(normalizeStatus(status)) ||
    STATUS_GROUPS.canceled.includes(normalizeStatus(status)) ||
    STATUS_GROUPS.archived.includes(normalizeStatus(status))
  );
}

/** Gibt alle erlaubten Folge-Status für Dropdowns zurück */
export function getNextStatusOptions(current) {
  const s = normalizeStatus(current);
  switch (s) {
    case "Offen":
      return ["Zugeteilt", "In Arbeit", "Storniert"];
    case "Zugeteilt":
      return ["In Arbeit", "Storniert"];
    case "In Arbeit":
      return ["Arbeiten erledigt", "Storniert"];
    case "Arbeiten erledigt":
      return ["Rechnung geschickt", "Abgeschlossen"];
    case "Rechnung geschickt":
      return ["Abgeschlossen", "Archiviert"];
    case "Abgeschlossen":
      return ["Archiviert"];
    default:
      return [];
  }
}

/* ───────────── Rechnungsflow: Transition-Buttons ───────────── */

/** 🏢 Rechnungs-Owner Typen */
export const RECHNUNG_OWNER_TYPES = {
  PL: "PL",
  BUERO: "BUERO",
};

/** 🏢 Labels für Rechnungs-Owner */
export const RECHNUNG_OWNER_LABELS = {
  PL: "Projektleiter",
  BUERO: "Büro",
};

/**
 * 🏢 Gibt die verfügbaren Transition-Buttons zurück, basierend auf:
 * - aktuellem Status
 * - Rolle des Users
 * - ob User der Projektleiter ist
 *
 * @param {string} currentStatus
 * @param {number} roleId
 * @param {boolean} isProjectLead - ist der User der Projektleiter dieses Auftrags?
 * @returns {Array<{targetStatus: string, label: string, variant: string}>}
 */
export function getTransitionButtons(currentStatus, roleId, isProjectLead = false, service = null) {
  const s = normalizeStatus(currentStatus);
  const isPower = [ROLE_ADMIN, ROLE_GL, ROLE_PL, ROLE_SEK].includes(roleId);
  const isBuero = [ROLE_ADMIN, ROLE_GL, ROLE_SEK].includes(roleId); // Admin, GL, Sek (ohne PL!)
  const isPL = roleId === ROLE_PL;
  const isRechnungOwnerPL = service?.rechnung_owner_type === "PL";
  const buttons = [];

  switch (s) {
    // ── Frühe Phasen ──
    case "Offen":
      if (isPower) {
        buttons.push({ targetStatus: "In Arbeit", label: "In Arbeit setzen", variant: "primary" });
      }
      break;

    case "Zugeteilt":
      if (isPower || isProjectLead) {
        buttons.push({ targetStatus: "In Arbeit", label: "In Arbeit setzen", variant: "primary" });
      }
      break;

    // ── Arbeitsphase ──
    case "In Arbeit":
      if (isPower || isProjectLead) {
        buttons.push({ targetStatus: "Arbeiten erledigt", label: "Arbeiten erledigt", variant: "success" });
      }
      break;

    // ── Rechnungsphase ──
    case "Arbeiten erledigt":
      // Rechnung versendet: Büro/GL/Admin ODER PL wenn er Rechnungs-Owner ist
      if (isBuero || (isPL && isRechnungOwnerPL) || isProjectLead && isRechnungOwnerPL) {
        buttons.push({ targetStatus: "Rechnung geschickt", label: "Rechnung versendet", variant: "primary" });
      }
      // Direkt abschließen (wenn Rechnung nicht nötig)
      if (isBuero) {
        buttons.push({ targetStatus: "Abgeschlossen", label: "Bezahlt / Abschliessen", variant: "success" });
      }
      break;

    case "Rechnung geschickt":
      // Bezahlt: Büro/GL/Admin
      if (isBuero) {
        buttons.push({ targetStatus: "Abgeschlossen", label: "Bezahlt", variant: "success" });
      }
      break;

    // ── Abschluss ──
    case "Abgeschlossen":
      if (isPower) {
        buttons.push({ targetStatus: "Archiviert", label: "Archivieren", variant: "default" });
      }
      break;

    case "Storniert":
      if (isPower) {
        buttons.push({ targetStatus: "Archiviert", label: "Archivieren", variant: "default" });
      }
      break;

    default:
      break;
  }

  return buttons;
}

/**
 * 🏢 Prüft ob ein Status zur Debitoren-Queue gehört (Rechnung versendet & unbezahlt)
 */
export function isDebitoren(service) {
  return normalizeStatus(service?.status) === "Rechnung geschickt" && !service?.bezahlt_am;
}

/* ───────────── FALLTYP: Abrechnungs-Kategorie für Serviceaufträge ───────────── */

export const FALLTYP_VALUES = ["NORMAL", "GARANTIE", "KULANZ"];

export const FALLTYP_LABELS = {
  NORMAL:   "Normal",
  GARANTIE: "Garantie",
  KULANZ:   "Kulanz",
};

export const FALLTYP_COLORS = {
  NORMAL:   null,
  GARANTIE: "bg-orange-500/20 text-orange-200 border-orange-400/30",
  KULANZ:   "bg-purple-500/20 text-purple-200 border-purple-400/30",
};

export const FALLTYP_COLORS_LIGHT = {
  NORMAL:   null,
  GARANTIE: "bg-orange-100 text-orange-800 border-orange-300",
  KULANZ:   "bg-purple-100 text-purple-800 border-purple-300",
};

/* ───────────── MÄNGEL: Werte aus @shared ───────────── */
export const MANGEL_STATUS_VALUES = [...MAENGEL_STATUS_VALUES];

export const MANGEL_STATUS_ALIAS = new Map(MAENGEL_STATUS_ALIAS_ENTRIES);

const MAENGEL_TRANSITIONS = buildMaengelTransitionsMap();

/** Alle serverseitig erlaubten Ziel-Status ab `current` (Shared-Map). */
export function getMaengelAllowedNextStatuses(current) {
  const s = normalizeMaengelStatus(current) || "Offen";
  return [...(MAENGEL_TRANSITIONS.get(s) || [])];
}

/** Alle Status, von denen ein Übergang nach `current` erlaubt ist. */
export function getMaengelAllowedPrevStatuses(current) {
  const s = normalizeMaengelStatus(current) || "Offen";
  const prev = [];
  for (const [from, targets] of MAENGEL_ALLOWED_TRANSITIONS_ENTRIES) {
    if (targets.includes(s)) prev.push(from);
  }
  return prev;
}

/**
 * Kompatibilitäts-Wrapper: erster erlaubter Schritt auf dem Hauptpfad
 * (Offen → In Arbeit → Mängel behoben → Bericht gesendet → Archiviert).
 */
export function getMaengelNextStatus(current) {
  const s = normalizeMaengelStatus(current) || "Offen";
  const allowed = getMaengelAllowedNextStatuses(s);
  if (!allowed.length) return [];
  const idx = MANGEL_STATUS_VALUES.indexOf(s);
  for (let i = idx + 1; i < MANGEL_STATUS_VALUES.length; i++) {
    const candidate = MANGEL_STATUS_VALUES[i];
    if (allowed.includes(candidate)) return [candidate];
  }
  return [allowed[0]];
}

/**
 * Kompatibilitäts-Wrapper: ein Schritt zurück auf dem Hauptpfad, falls erlaubt.
 */
export function getMaengelPrevStatus(current) {
  const s = normalizeMaengelStatus(current) || "Offen";
  const allowed = getMaengelAllowedPrevStatuses(s);
  if (!allowed.length) return null;
  const idx = MANGEL_STATUS_VALUES.indexOf(s);
  if (idx > 0) {
    const mainPrev = MANGEL_STATUS_VALUES[idx - 1];
    if (allowed.includes(mainPrev)) return mainPrev;
  }
  return allowed[0];
}

export function normalizeMaengelStatus(s) {
  if (s == null) return "";
  const raw = String(s).trim();
  if (!raw) return "";
  const exact = MANGEL_STATUS_VALUES.find((x) => x.toLowerCase() === raw.toLowerCase());
  if (exact) return exact;
  const alias = MANGEL_STATUS_ALIAS.get(raw.toLowerCase());
  return alias || "";
}

export function getMaengelSelectableStatus(current) {
  return getMaengelAllowedNextStatuses(current);
}


/** 🔹 Für Screens, die *alle* Stati erlauben (z. B. Mängel) */
export function getSelectableStatus(current, { entity = "generic", free = false } = {}) {
  // Für Mängel: eigene Liste
  if (entity === "maengel" || entity === "maengel_report") return getMaengelSelectableStatus(current);
  // Freie Auswahl (z. B. Admin-Tools)
  if (free) return [...STATUS_VALUES];
  // Für andere Entitäten (z. B. Service) Standard-Übergänge + „Archiviert“ immer erlauben
  const next = new Set([...getNextStatusOptions(current), "Archiviert"]);
  return [...next].filter(Boolean);
}

// 🔹 Offerten-Status (@shared)
export const OFFERTE_STATUS_VALUES = [...OFFERTE_STATUS_VALUES_SHARED];

export const OFFERTE_STATUS_ALIAS = new Map(OFFERTE_STATUS_ALIAS_ENTRIES);

export const OFFERTE_STATUS_COLORS = {
  Entwurf:    "bg-blue-500/20 text-blue-100 border-blue-400/30",
  Versendet:  "bg-sky-500/20 text-sky-100 border-sky-400/30",
  Zuschlag:   "bg-green-500/25 text-green-100 border-green-400/30",
  Abgelehnt:  "bg-rose-500/25 text-rose-100 border-rose-400/30",
  Archiviert: "bg-zinc-500/25 text-zinc-200 border-zinc-400/30",
};

export function normalizeOfferteStatus(s) {
  if (s == null) return "";
  const raw = String(s).trim();
  if (!raw) return "";
  const exact = OFFERTE_STATUS_VALUES.find(
    (x) => x.toLowerCase() === raw.toLowerCase()
  );
  if (exact) return exact;
  const alias = OFFERTE_STATUS_ALIAS.get(raw.toLowerCase());
  return alias || raw;
}

export function getOfferteStatusColor(status) {
  const s = normalizeOfferteStatus(status);
  return OFFERTE_STATUS_COLORS[s] || "bg-white/10 text-blue-100 border-white/15";
}

export function prettyOfferteStatus(status) {
  return normalizeOfferteStatus(status);
}

// optional, falls du irgendwo Auswahl brauchst
export function getOfferteSelectableStatus(current, { free = false } = {}) {
  if (free) return [...OFFERTE_STATUS_VALUES];
  // bewusst schlank: alles möglich, keine komplizierten Übergänge
  return [...OFFERTE_STATUS_VALUES];
}

